import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';

/**
 * Idle-timeout auto-logout.
 *
 * • Any user interaction (mouse, keyboard, scroll, touch, route change) counts
 *   as activity. We coalesce updates via a throttle to keep this cheap even on
 *   busy pages.
 * • After IDLE_MS of inactivity we show a warning modal with a WARN_MS
 *   countdown. If the user clicks "Stay signed in" we reset. If the countdown
 *   hits zero we call logout() and route to the tenant's login page with a
 *   toast-worthy query flag.
 * • Only runs while a user is actually authenticated. On the public marketing
 *   site or login page the component simply renders null.
 *
 * Numbers chosen deliberately (per product decision): 30 min idle, 30 sec
 * warning. Matches banking / healthcare SaaS norms for a CRM holding PII +
 * financial data.
 */
const IDLE_MS = 30 * 60 * 1000; // 30 minutes
const WARN_MS = 30 * 1000; // 30 second warning countdown
const THROTTLE_MS = 5000; // don't update lastActivity more than once per 5s

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'];

export default function IdleTimeoutManager() {
  const { isAuthenticated, logout } = useAuthStore();
  const nav = useNavigate();
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(WARN_MS / 1000));

  // Bump the activity timestamp — throttled to once per 5s.
  const bump = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current >= THROTTLE_MS) {
      lastActivityRef.current = now;
    }
  }, []);

  // Reset everything and stay signed in.
  const stay = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarningOpen(false);
    setSecondsLeft(Math.round(WARN_MS / 1000));
  }, []);

  // Force-logout — clear auth + route to the tenant login (or global /login).
  const forceLogout = useCallback(() => {
    logout();
    // Best-effort tenant-scoped login redirect. Marketing routes stay at /login.
    const m = location.pathname.match(/^\/t\/([^/]+)/);
    const target = m ? `/t/${m[1]}/login?reason=idle` : '/login?reason=idle';
    nav(target, { replace: true });
  }, [logout, nav, location.pathname]);

  // Attach activity listeners while authenticated.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
  }, [isAuthenticated, bump]);

  // Route changes count as activity too.
  useEffect(() => {
    if (isAuthenticated) lastActivityRef.current = Date.now();
  }, [location.pathname, isAuthenticated]);

  // Main tick — checks idle status every 5s.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const iv = setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= IDLE_MS && !warningOpen) {
        setWarningOpen(true);
        setSecondsLeft(Math.round(WARN_MS / 1000));
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [isAuthenticated, warningOpen]);

  // Countdown while warning is open. If it reaches 0, force-logout.
  useEffect(() => {
    if (!warningOpen) return undefined;
    const start = Date.now();
    const iv = setInterval(() => {
      const remaining = Math.max(0, WARN_MS - (Date.now() - start));
      const secs = Math.ceil(remaining / 1000);
      setSecondsLeft(secs);
      if (remaining <= 0) {
        clearInterval(iv);
        forceLogout();
      }
    }, 250);
    return () => clearInterval(iv);
  }, [warningOpen, forceLogout]);

  if (!isAuthenticated || !warningOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
      role="alertdialog"
      aria-labelledby="idle-title"
      data-testid="idle-warning-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E2F3F0] flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center shrink-0">
            <Clock size={22} weight="duotone" className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 id="idle-title" className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              You're about to be signed out
            </h3>
            <p className="text-sm text-[#3B5A54] mt-1">
              For your security we sign you out after 30&nbsp;minutes of inactivity.
              You'll be logged out in{' '}
              <b className="text-amber-700" data-testid="idle-countdown">{secondsLeft}s</b>.
            </p>
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end gap-2 bg-[#F4F9F7]">
          <button
            onClick={forceLogout}
            className="px-4 py-2 text-sm font-medium text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-white transition"
            data-testid="idle-logout-btn"
          >
            Log out now
          </button>
          <button
            onClick={stay}
            className="px-5 py-2 text-sm font-semibold text-white bg-[#00a69c] rounded-lg hover:bg-[#008f86] transition"
            data-testid="idle-stay-btn"
            autoFocus
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
